import { Divider, Stack, Typography } from "@mui/material"
import CustomIconButton from "components/CustomIconButton"
import { useBizTypeTranslation } from "hooks/useBizTypeTranslation";
import { useEffect, useRef, useState } from "react";
import { apiUrl } from "config/settings";
import http from "../../../http"
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { setShowLoading, setShowMassage } from "store/sysSlice";
import CustomButton from "components/CustomButton";
import { MC_SOFTWARE_TYPE } from "config/const";
import NullCheck from "components/NullCheck";
import {
    AcUnit,
    AutofpsSelect,
    ExpandLess,
    ExpandMore,
    InvertColorsOffRounded,
    KeyboardDoubleArrowUp,
    LightMode,
    ModeStandby,
    OpacityRounded,
    PowerSettingsNew,
    WindPower
} from "@mui/icons-material"

const Ac = ({ data, isConnected, sendTimerRef, checkWeeklyTime }) => {
    const { bt } = useBizTypeTranslation();
    const dispatch = useDispatch()
    const bizPath = useSelector((state) => state?.biz?.data?.path);
    const holdIntervalRef = useRef(null);
    const lastSentAtRef = useRef(0);
    const isInteractingRef = useRef(false);

    const [isDHT, setIsDHT] = useState(false)
    const [fanEmpOpacity, setFanEmpOpacity] = useState(0.1)
    const [fanAutoFanOpacity, setFanAutoFanOpacity] = useState(0.1)
    const [fanHighOpacity, setFanHighOpacity] = useState(0.1)
    const [fanMediumOpacity, setFanMediumOpacity] = useState(0.1)
    const [fanLowOpacity, setFanLowOpacity] = useState(0.1)
    const [modeAutoOpacity, setModeAutoOpacity] = useState(0.1)
    const [modeVentOpacity, setModeVentOpacity] = useState(0.1)
    const [modeHeatOpacity, setModeHeatOpacity] = useState(0.1)
    const [modeCoolOpacity, setModeCoolOpacity] = useState(0.1)
    const [modeDehumidifyOpacity, setModeDehumidifyOpacity] = useState(0.1)
    const [modeHumidifyOpacity, setModeHumidifyOpacity] = useState(0.1)

    const [localSetPoint, setLocalSetPoint] = useState(data?.ac?.setPointActive ?? data?.acSettings?.minSetPoint);

    useEffect(() => {
        if (data) {
            if (data?.softwareType === MC_SOFTWARE_TYPE.B1_8) setIsDHT(true)

            if (!data?.turnOnDate) {
                setModeAutoOpacity(0.1)
                setModeVentOpacity(0.1)
                setModeHeatOpacity(0.1)
                setModeCoolOpacity(0.1)
                setFanAutoFanOpacity(0.1)
                setFanHighOpacity(0.1)
                setFanMediumOpacity(0.1)
                setFanLowOpacity(0.1)
                setFanEmpOpacity(0.1)
            }
            else {
                setFanEmpOpacity(1)
            }

            if (data?.turnOnDate) {
                if (data?.ac?.fanSpeedActive === "low") {
                    setFanLowOpacity(1)
                    setFanMediumOpacity(0.1)
                    setFanHighOpacity(0.1)
                    setFanAutoFanOpacity(0.1)
                } else if (data?.ac?.fanSpeedActive === "medium") {
                    setFanLowOpacity(1)
                    setFanMediumOpacity(1)
                    setFanHighOpacity(0.1)
                    setFanAutoFanOpacity(0.1)
                } else if (data?.ac?.fanSpeedActive === "high") {
                    setFanLowOpacity(1)
                    setFanMediumOpacity(1)
                    setFanHighOpacity(1)
                    setFanAutoFanOpacity(0.1)
                } else if (data?.ac?.fanSpeedActive === "autoFan") {
                    setFanAutoFanOpacity(1)
                    setFanLowOpacity(0.1)
                    setFanMediumOpacity(0.1)
                    setFanHighOpacity(0.1)
                }

                if (data?.ac?.modeActive === "COOL") {
                    setModeCoolOpacity(1)
                    setModeHeatOpacity(0.1)
                    setModeVentOpacity(0.1)
                    setModeAutoOpacity(0.1)
                    setModeHumidifyOpacity(0.1)
                    setModeDehumidifyOpacity(0.1)
                } else if (data?.ac?.modeActive === "HEAT") {
                    setModeHeatOpacity(1)
                    setModeCoolOpacity(0.1)
                    setModeVentOpacity(0.1)
                    setModeAutoOpacity(0.1)
                    setModeHumidifyOpacity(0.1)
                    setModeDehumidifyOpacity(0.1)
                } else if (data?.ac?.modeActive === "DEHUMIDIFY") {
                    setModeDehumidifyOpacity(1)
                    setModeHumidifyOpacity(0.1)
                    setModeVentOpacity(0.1)
                    setModeHeatOpacity(0.1)
                    setModeCoolOpacity(0.1)
                    setModeAutoOpacity(0.1)
                } else if (data?.ac?.modeActive === "HUMIDIFY") {
                    setModeHumidifyOpacity(1)
                    setModeDehumidifyOpacity(0.1)
                    setModeHeatOpacity(0.1)
                    setModeCoolOpacity(0.1)
                    setModeVentOpacity(0.1)
                    setModeAutoOpacity(0.1)
                } else if (data?.ac?.modeActive === "vent") {
                    setModeVentOpacity(1)
                    setModeHeatOpacity(0.1)
                    setModeCoolOpacity(0.1)
                    setModeAutoOpacity(0.1)
                } else if (data?.ac?.modeActive === "auto") {
                    setModeAutoOpacity(1)
                    setModeVentOpacity(0.1)
                    setModeHeatOpacity(0.1)
                    setModeCoolOpacity(0.1)
                }
            }
        }
    }, [data])

    useEffect(() => {
        if (!isInteractingRef.current) {
            setLocalSetPoint(data?.ac?.setPointActive ?? data?.acSettings?.minSetPoint);
        }
    }, [data?.ac?.setPointActive, data?.acSettings?.minSetPoint]);

    useEffect(() => {
        return () => {
            if (sendTimerRef.current) clearTimeout(sendTimerRef.current);
            if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);
        }
    }, []);

    const scheduleSetPointSend = (value) => {
        // update UI optimistically
        setLocalSetPoint(value);

        // clear any existing timer
        if (sendTimerRef.current) clearTimeout(sendTimerRef.current);

        // debounce: wait 700ms of "quiet" before sending final value
        sendTimerRef.current = setTimeout(() => {
            const now = Date.now();
            // simple throttle: ensure at least 400ms between actual sends
            if (now - lastSentAtRef.current < 400) {
                sendTimerRef.current = setTimeout(() => {
                    lastSentAtRef.current = Date.now();
                    onClickHandle("setPointActive", value);
                    sendTimerRef.current = null;
                }, 400);
            } else {
                lastSentAtRef.current = now;
                onClickHandle("setPointActive", value);
                sendTimerRef.current = null;
            }
        }, 700);
    };

    const handlePressStart = (direction) => {
        isInteractingRef.current = true;
        singleStepLocal(direction);
        if (holdIntervalRef.current) clearInterval(holdIntervalRef.current);

        holdIntervalRef.current = setInterval(() => {
            setLocalSetPoint(prev => {
                const max = data?.acSettings?.maxSetPoint ?? 30;
                const min = data?.acSettings?.minSetPoint ?? 16;
                let newVal = (typeof prev === "number" ? prev : (data?.ac?.setPointActive ?? min));
                newVal = direction === "up" ? newVal + 1 : newVal - 1;
                if (newVal > max) newVal = max;
                if (newVal < min) newVal = min;
                scheduleSetPointSend(newVal);
                return newVal;
            });
        }, 200);
    };

    const handlePressEnd = () => {
        isInteractingRef.current = false;
        if (holdIntervalRef.current) {
            clearInterval(holdIntervalRef.current);
            holdIntervalRef.current = null;
        }
        // do NOT force-send here; debounce will send last value
    };

    const singleStepLocal = (direction) => {
        const max = data?.acSettings?.maxSetPoint ?? 30;
        const min = data?.acSettings?.minSetPoint ?? 16;
        let current = (typeof localSetPoint === "number") ? localSetPoint : (data?.ac?.setPointActive ?? min);
        let newVal = direction === "up" ? current + 1 : current - 1;
        if (newVal > max) newVal = max;
        if (newVal < min) newVal = min;
        scheduleSetPointSend(newVal);
    };

    const onClickHandle = async (type, value, overrideSchedule = false) => {
        if (!data?.isConnected)
            return dispatch(setShowMassage({ massage: "MC_IS_DISCONNECTED", type: "error" }));

        if (!isConnected)
            return dispatch(setShowMassage({ massage: "WS_IS_NOT_CONNECTED", type: "info" }));

        let item = {
            overrideSchedule,
            turnOnDate: data?.turnOnDate,
            turnOffDate: data?.turnOffDate,
            setPointActive: data?.ac?.setPointActive,
            modeActive: data?.ac?.modeActive,
            fanSpeedActive: data?.ac?.fanSpeedActive,
            command: null,
            commandValue: null
        }

        if (type === 'on') {
            item.turnOnDate = moment()
            item.turnOffDate = null
            item.command = "on"
        }
        else if (type === 'off') {
            item.turnOffDate = moment()
            item.turnOnDate = null
            item.command = "off"
        }
        else if (type === 'setPointActive') {
            item.setPointActive = value
            item.command = "setPointActive"
            item.commandValue = value
        }
        else if (type === 'mode') {
            item.modeActive = value
            item.command = "modeActive"
            item.commandValue = value
        }
        else if (type === 'fan') {
            item.fanSpeedActive = value
            item.command = "fanActive"
            item.commandValue = value
        }

        if ("vibrate" in navigator) navigator.vibrate(100)

        dispatch(setShowLoading(true));
        try {
            await http.patch(`${apiUrl}/mc/sendCommand/${bizPath}/${data?.guid}`, item)
        } catch (error) {
            // אפשר להראות שגיאה כאן אם רוצים
        } finally {
            dispatch(setShowLoading(false));
        }
    }

    const powerkHandle = async (value) => {
        if (sendTimerRef.current) {
            clearTimeout(sendTimerRef.current);
            sendTimerRef.current = null;
        }
        if (holdIntervalRef.current) {
            clearInterval(holdIntervalRef.current);
            holdIntervalRef.current = null;
        }
        if (data?.hasControllerSchedule) {
            await checkWeeklyTime({ callbackFunc: ({ overrideSchedule = false } = {}) => onClickHandle(value, value, overrideSchedule) });
        } else onClickHandle(value, value)
    };

    const findNextMode = async () => {
        let value = ""
        if (isDHT) {
            value = data?.ac?.modeActive || "DEHUMIDIFY";
            onClickHandle("mode", value === "DEHUMIDIFY" ? "HUMIDIFY" : "DEHUMIDIFY")
        }

        else {
            value = data?.ac?.modeActive || "COOL";
            onClickHandle("mode", value === "COOL" ? "HEAT" : "COOL")
        }
    };

    const findNextFan = async () => {
        let value;
        let nextIndex;

        const keys = Object.keys(data?.ac?.commands?.fanSpeed || {});
        const index = keys.indexOf(data?.ac?.fanSpeedActive);

        if (index === -1) {
            value = keys[0]
        } else if (keys[index + 1]) {
            nextIndex = index + 1;
            value = keys[nextIndex];
        } else {
            value = keys[0]
        }
        onClickHandle("fan", value)
    };

    return (
        <Stack justifyContent='center' alignItems='center' spacing={3}>
            <Stack
                width={200}
                height={150}
                direction='row'
                borderRadius={5}
                border={2}
                justifyContent='space-between'
            >

                <Stack paddingBottom={0.5} position='relative' justifyContent='end' alignItems='center'>
                    <AutofpsSelect sx={{ transform: 'translate(0px, 20px)', fontSize: 35, opacity: fanAutoFanOpacity }} />
                    <KeyboardDoubleArrowUp sx={{ transform: 'translate(0px, 20px)', fontSize: 40, opacity: fanHighOpacity }} />
                    <KeyboardDoubleArrowUp sx={{ transform: 'translate(0px, 10px)', fontSize: 40, opacity: fanMediumOpacity }} />
                    <KeyboardDoubleArrowUp sx={{ fontSize: 40, opacity: fanLowOpacity }} />
                </Stack>

                <Divider orientation="vertical" flexItem />

                <Stack
                    sx={{ opacity: fanEmpOpacity }}
                    width='100%'
                    alignItems='center'
                    justifyContent='center'
                >
                    <Typography variant="h6">
                        {`${bt(isDHT ? "DHT_ACTIVE" : "TEMP_ACTIVE")}`}
                    </Typography>
                    <Typography variant="h3" fontWeight='bold'>
                        {typeof localSetPoint === "number"
                            ? `${localSetPoint}${isDHT ? "%" : "°"}`
                            : (data?.ac?.setPointActive ? `${data?.ac?.setPointActive}${isDHT ? "%" : "°"}` : "----")}
                    </Typography>
                </Stack>

                <Divider orientation="vertical" flexItem />

                <NullCheck valueForCheck={!isDHT}>
                    <Stack padding={0.5} position='relative' justifyContent='space-between' alignItems='center'>
                        <AutofpsSelect sx={{ fontSize: 35, opacity: modeAutoOpacity }} />
                        <WindPower sx={{ fontSize: 25, opacity: modeVentOpacity }} />
                        <LightMode sx={{ fontSize: 25, opacity: modeHeatOpacity }} />
                        <AcUnit sx={{ fontSize: 25, opacity: modeCoolOpacity }} />
                    </Stack>
                </NullCheck>

                <NullCheck valueForCheck={isDHT}>
                    <Stack padding={0.5} position='relative' justifyContent='end' alignItems='center' spacing={2}>
                        <InvertColorsOffRounded sx={{ fontSize: 25, opacity: modeDehumidifyOpacity }} />
                        <OpacityRounded sx={{ fontSize: 25, opacity: modeHumidifyOpacity }} />
                    </Stack>
                </NullCheck>

            </Stack>

            <CustomButton
                disabled={sendTimerRef.current > 0}
                startIcon={<PowerSettingsNew
                    sx={{ color: data?.turnOnDate && data?.isConnected ? 'success.text' : 'error.text' }}
                />}
                title={data?.turnOnDate && data?.isConnected ? bt("ON_MC") : bt("OFF_MC")}
                fullWidth
                height={40}
                color={data?.turnOnDate && data?.isConnected ? 'success.main' : 'error.main'}
                textColor={data?.turnOnDate && data?.isConnected ? 'success.text' : 'error.text'}
                onClick={() => powerkHandle(data?.turnOnDate ? 'off' : 'on')}
            />

            <Stack justifyContent='center' alignItems='center' spacing={1}>
                <CustomIconButton
                    disabled={data?.turnOffDate || (localSetPoint >= (data?.acSettings?.maxSetPoint ?? 30))}
                    icon={<ExpandLess sx={{ color: "bg.main" }} fontSize="large" />}
                    onClick={() => singleStepLocal('up')}
                    onMouseDown={() => handlePressStart('up')}
                    onMouseUp={handlePressEnd}
                    onMouseLeave={handlePressEnd}
                    onTouchStart={() => handlePressStart('up')}
                    onTouchEnd={handlePressEnd}
                />

                <Stack direction='row' spacing={7}>
                    <CustomIconButton
                        disabled
                        onClick={findNextFan}
                        icon={<WindPower sx={{ color: "bg.main" }} fontSize="large" />}
                    />
                    <CustomIconButton
                        disabled={sendTimerRef.current > 0 || !data?.turnOnDate}
                        onClick={findNextMode}
                        icon={<ModeStandby sx={{ color: "bg.main" }} fontSize="large" />}
                    />
                </Stack>

                <CustomIconButton
                    disabled={data?.turnOffDate || (localSetPoint <= (data?.acSettings?.minSetPoint ?? 16))}
                    icon={<ExpandMore sx={{ color: "bg.main" }} fontSize="large" />}
                    onClick={() => singleStepLocal('down')}
                    onMouseDown={() => handlePressStart('down')}
                    onMouseUp={handlePressEnd}
                    onMouseLeave={handlePressEnd}
                    onTouchStart={() => handlePressStart('down')}
                    onTouchEnd={handlePressEnd}
                />

            </Stack>
        </Stack>
    )
}
export default Ac
